// stage / join ビューの切替、ブラウザ拡大 (zoom) 検知、グリッドサイズ調整、
// オーバーレイ (ツールバー) の自動非表示を担当する。
//
// ブラウザ拡大対応の戦略: .video-grid に baseline ビューポート (= 拡大率 1 換算の
// CSS px) を inline width / height で固定する。ブラウザを拡大すると
// window.innerWidth は縮むが grid は baseline px のまま → .stage 側にはみ出して
// スクロール (= パン)。縮小時は grid が viewport より小さくなり中央寄せ。

import { resetStageZoom } from "./stage-zoom.js";

type ViewportEls = {
  joinView: HTMLElement;
  stageView: HTMLElement;
  stageOverlay: HTMLDivElement;
  videoGrid: HTMLDivElement;
};

let els: ViewportEls;
// ページロード時の devicePixelRatio を「等倍」と見なし、それより大きければ拡大中。
let baselineDPR = 1;
let overlayHideTimer: number | null = null;

export function initViewport(deps: ViewportEls): void {
  els = deps;
  baselineDPR = window.devicePixelRatio;
}

export function showStage(): void {
  els.joinView.hidden = true;
  els.stageView.hidden = false;
  applyZoomState();
}

export function showJoin(): void {
  els.stageView.hidden = true;
  els.joinView.hidden = false;
  if (overlayHideTimer !== null) {
    window.clearTimeout(overlayHideTimer);
    overlayHideTimer = null;
  }
  els.stageOverlay.dataset.hidden = "false";
  resetStageZoom();
}

export function scheduleOverlayHide(): void {
  // 拡大中は overlay を出さない (CSS 側で display:none。dataset も合わせる)
  if (isZoomedIn()) {
    els.stageOverlay.dataset.hidden = "true";
    return;
  }
  els.stageOverlay.dataset.hidden = "false";
  if (overlayHideTimer !== null) {
    window.clearTimeout(overlayHideTimer);
  }
  overlayHideTimer = window.setTimeout(() => {
    els.stageOverlay.dataset.hidden = "true";
  }, 2500);
}

export function isZoomedIn(): boolean {
  return window.devicePixelRatio > baselineDPR + 0.01;
}

// baseline ビューポートを CSS px で取得。
// zoom 倍率 = devicePixelRatio / baselineDPR。
// innerWidth は現在の zoom で縮んでいるので、その倍率を掛けて等倍時の値に戻す。
function getBaselineViewport(): { w: number; h: number } {
  const zoom = window.devicePixelRatio / baselineDPR;
  return {
    w: window.innerWidth * zoom,
    h: window.innerHeight * zoom,
  };
}

export function sizeGridToViewport(): void {
  if (els.stageView.hidden) return;
  const layout = els.videoGrid.dataset.layout ?? "";
  // 3D room はそれ自体が絶対 px の壁構造を持つので grid 自体は親に追従させる
  // (CSS 側で width: 100% / height: 100% 指定)。
  if (layout === "room") {
    els.videoGrid.style.removeProperty("width");
    els.videoGrid.style.removeProperty("height");
    return;
  }
  const count = Number(els.videoGrid.dataset.count || "0");
  if (count === 0) {
    els.videoGrid.style.removeProperty("width");
    els.videoGrid.style.removeProperty("height");
    return;
  }

  const { w: baselineW, h: baselineH } = getBaselineViewport();
  if (baselineW === 0 || baselineH === 0) return;

  if (layout === "free") {
    // 自由配置はステージ全面を使う。baseline サイズで固定。
    els.videoGrid.style.width = `${baselineW}px`;
    els.videoGrid.style.height = `${baselineH}px`;
    return;
  }

  // 固定レイアウト: 設定済みの aspect-ratio で baseline ビューポートに contain fit
  const aspectStr = els.videoGrid.style.aspectRatio;
  let aspect = parseFloat(aspectStr);
  if (!Number.isFinite(aspect) || aspect <= 0) {
    aspect = baselineW / baselineH;
  }
  let w: number;
  let h: number;
  if (baselineW / baselineH > aspect) {
    h = baselineH;
    w = h * aspect;
  } else {
    w = baselineW;
    h = w / aspect;
  }
  els.videoGrid.style.width = `${w}px`;
  els.videoGrid.style.height = `${h}px`;
}

export function applyZoomState(): void {
  const zoomed = isZoomedIn();
  els.stageView.dataset.zoomed = zoomed ? "true" : "false";
  if (zoomed) {
    if (overlayHideTimer !== null) {
      window.clearTimeout(overlayHideTimer);
      overlayHideTimer = null;
    }
    els.stageOverlay.dataset.hidden = "true";
  } else {
    scheduleOverlayHide();
  }
}
